import { useContext, useEffect, useState } from "react";
import EnterpriseService from "../../common/services/enterprise-service";
import { EnterpriseContext } from "./EnterpriseContext";
import { useNavigate } from "react-router";
import Paginator from "../ui/Paginator";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import LoadingOverlay from "react-loading-overlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import { confirmWrapper } from "../ui/askConfirm";
import truckyService from "../../common/services/trucky-service";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import OrderByButtons from "../ui/OrderByButtons";
import BoxDynamicAd from "../../ads/BoxDynamicAd";

const CompanyAchievements = ({ container }) => {
  const es = new EnterpriseService();
  const { globalContext } = useContext(EnterpriseContext);
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });
  const [totalPages, setTotalPages] = useState(0);

  const [searchParameters, setSearchParameters] = useState({
    title: "",
    period: "",
    goal_type: "",
    sortingDirection: "desc",
    sortingField: "id",
  });

  useEffect(() => {
    truckyService.setDocumentTitle("Company Achievements");
  }, []);

  useEffect(() => {
    getAchievements();
  }, []);

  useEffect(() => {
    getAchievements();
  }, [
    pagination.page,
    searchParameters.goal_type,
    searchParameters.period,
    searchParameters.sortingField,
    searchParameters.sortingDirection,
  ]);

  const getAchievements = async () => {
    setLoading(true);
    const result = await es.getAllCompanyAchievements(
      globalContext.company.id,
      pagination.page,
      searchParameters
    );

    setAchievements(result.data);
    setTotalPages(result.last_page);
    setLoading(false);
  };

  const handlePageClick = (event) => {
    setPagination({ ...pagination, page: event.selected + 1 });
  };

  const deleteAchievement = async (ach) => {
    if (
      await confirmWrapper(container.localeManager.strings.areYouSure, {
        container: container,
      })
    ) {
      setLoading(true);
      const result = await es.deleteAchievement(globalContext.company, ach.id);
      if (result.success) {
        getAchievements();
      } else {
        toast.error(result.message);
      }
      setLoading(false);
    }
  };

  const changeSortingField = ({ field, direction }) => {
    setSearchParameters({
      ...searchParameters,
      sortingField: field,
      sortingDirection: direction,
    });
  };

  return (
    <>
      <LoadingOverlay
        active={loading}
        spinner={
          <Spinner
            color="primary"
            style={{ width: "4rem", height: "4rem" }}
          ></Spinner>
        }
      >
        <Row noGutters className="" id="enterpriseContainer">
          <VTCMenu
            active={"/company/achievements"}
            container={container}
          ></VTCMenu>

          <Col>
            <Row noGutters>
              <Col>
                <Card className="h-100">
                  <CardBody>
                    <Breadcrumbs
                      breadcrumbs={[
                        {
                          to: "/vtchub",
                          title: container.localeManager.strings.vtcHub,
                        },
                        {
                          title:
                            container.localeManager.strings.companyAchievements,
                        },
                      ]}
                    ></Breadcrumbs>
                    <div className="page-hero mb-4">
                      <span className="page-hero-icon">
                        <FontAwesomeIcon icon="trophy" className="mx-0" />
                      </span>
                      <div className="flex-1">
                        <h1 className="page-hero-title mb-0">{container.localeManager.strings.companyAchievements}</h1>
                      </div>
                      <div className="ms-auto">
                        <Button size="sm" color="primary" onClick={() => navigate('/company/achievements/create')}>
                          <FontAwesomeIcon icon="plus" className="me-1" />{container.localeManager.strings.createNewAchievement}
                        </Button>
                      </div>
                    </div>
                    <div className="filters-toolbar mb-3">
                    <FormGroup row>
                      <Col sm={3}>
                        <Label>{container.localeManager.strings.title}</Label>
                        <Input
                          type="text"
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              title: e.target.value,
                            })
                          }
                          value={searchParameters.name}
                        ></Input>
                      </Col>
                      <Col sm={3}>
                        <Label>{container.localeManager.strings.period}</Label>
                        <Input
                          value={searchParameters.period}
                          type="select"
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              period: e.target.value,
                            })
                          }
                        >
                          <option value="">
                            {container.localeManager.strings.selectOne}
                          </option>
                          <option value="monthly">
                            {container.localeManager.strings.monthly}
                          </option>
                          <option value="recurring_monthly">
                            {container.localeManager.strings.recurring_monthly}
                          </option>
                          <option value="weekly">
                            {container.localeManager.strings.weekly}
                          </option>
                          <option value="recurring_weekly">
                            {container.localeManager.strings.recurring_weekly}
                          </option>
                          <option value="yearly">
                            {container.localeManager.strings.yearly}
                          </option>
                          <option value="recurring_yearly">
                            {container.localeManager.strings.recurring_yearly}
                          </option>
                        </Input>
                      </Col>
                      <Col sm={3}>
                        <Label>
                          {container.localeManager.strings.goal_type}
                        </Label>
                        <Input
                          value={searchParameters.control_value_type}
                          type="select"
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              control_value_type: e.target.value,
                            })
                          }
                        >
                          <option value="">
                            {container.localeManager.strings.selectOne}
                          </option>
                          <option value="jobs_count">
                            {container.localeManager.strings.jobs_count}
                          </option>
                          <option value="mass">
                            {container.localeManager.strings.mass}
                          </option>
                          <option value="distance_km">
                            {container.localeManager.strings.distance_km}
                          </option>
                          <option value="revenue">
                            {container.localeManager.strings.revenue}
                          </option>
                          <option value="hardcore_simulation_points">HP</option>
                        </Input>
                      </Col>
                      <Col sm={3}>
                        <Label>{container.localeManager.strings.month}</Label>
                        <Input
                          value={searchParameters.month}
                          type="select"
                          onChange={(e) =>
                            setSearchParameters({
                              ...searchParameters,
                              month: e.target.value,
                            })
                          }
                        >
                          <option value="">
                            {container.localeManager.strings.selectOne}
                          </option>
                          {container.localeManager.moment
                            .months()
                            .map((m, index) => (
                              <option value={index + 1}>{m}</option>
                            ))}
                        </Input>
                      </Col>
                    </FormGroup>
                    <div className="my-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => getAchievements()}
                      >
                        {container.localeManager.strings.search}
                      </Button>
                    </div>
                    </div>
                    {!loading && (
                      <>
                        {totalPages > 1 && (
                          <div className="mb-3">
                            <Paginator
                              forcePage={pagination.page - 1}
                              totalPages={totalPages}
                              handlePageClick={handlePageClick}
                            />
                          </div>
                        )}
                        <Table
                          responsive
                          striped
                          size="sm"
                          className="table-row-dashed table-row-gray-300 align-middle gs-0"
                          hover
                        >
                          <thead>
                            <tr className="fw-bolder text-gray-700 bg-light">
                              <th className="ps-2">
                                #
                                <OrderByButtons
                                  container={container}
                                  currentSortingDirection={
                                    searchParameters.sortingDirection
                                  }
                                  currentSortingField={
                                    searchParameters.sortingField
                                  }
                                  field="id"
                                  onClick={changeSortingField}
                                />
                              </th>
                              <th>
                                {container.localeManager.strings.title}
                                <OrderByButtons
                                  container={container}
                                  currentSortingDirection={
                                    searchParameters.sortingDirection
                                  }
                                  currentSortingField={
                                    searchParameters.sortingField
                                  }
                                  field="title"
                                  onClick={changeSortingField}
                                />
                              </th>
                              <th>{container.localeManager.strings.period}</th>
                              <th>
                                {container.localeManager.strings.goal}
                                <OrderByButtons
                                  container={container}
                                  currentSortingDirection={
                                    searchParameters.sortingDirection
                                  }
                                  currentSortingField={
                                    searchParameters.sortingField
                                  }
                                  field="control_value"
                                  onClick={changeSortingField}
                                />
                              </th>
                              <th>
                                {container.localeManager.strings.goal_type}
                                <OrderByButtons
                                  container={container}
                                  currentSortingDirection={
                                    searchParameters.sortingDirection
                                  }
                                  currentSortingField={
                                    searchParameters.sortingField
                                  }
                                  field="control_value_type"
                                  onClick={changeSortingField}
                                />
                              </th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {achievements.map((m) => {
                              return (
                                <tr>
                                  <td className="ps-2">{m.id}</td>
                                  <td>{m.computed_title}</td>
                                  <td>
                                    {container.localeManager.strings[m.period]}
                                  </td>
                                  <td>{m.control_value}</td>
                                  <td>
                                    {
                                      container.localeManager.strings[
                                        m.control_value_type
                                      ]
                                    }
                                  </td>
                                  <td className="text-end">
                                    <Button
                                      onClick={() =>
                                        navigate(
                                          `/company/achievements/${m.id}`
                                        )
                                      }
                                      className="btn-icon w-30px h-30px ms-auto"
                                      size="sm"
                                      color="active-light-primary"
                                    >
                                      <FontAwesomeIcon
                                        icon="pencil-alt"
                                        className="me-0"
                                      />
                                    </Button>
                                    <Button
                                      onClick={() => deleteAchievement(m)}
                                      className="btn-icon w-30px h-30px ms-auto"
                                      size="sm"
                                      color="active-light-danger"
                                    >
                                      <FontAwesomeIcon
                                        icon="trash-alt"
                                        className="me-0"
                                      />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
          <aside className="ad-column-placeholder">
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
        </Row>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </LoadingOverlay>
    </>
  );
};

export default CompanyAchievements;
